import { IRecordOf, RecordOf } from '@opengov/compose-app/immutable';
import { List } from 'immutable';
import { BasicColumnTypes, IOgqlColumn, IOgqlSemanticType } from '@opengov/ogql-types';

import { getColumnName } from './reportColumns-immer';
import { IColumnAlias } from './reportConfiguration';
import { IDatasetLensColumn } from './reportDataset';

export const ORGANIZED_BY_COLUMN_ALIAS = 'org__by_og';
export const CATEGORIZED_BY_COLUMN_ALIAS = 'cat_e_gory_og';
export const AGGREGATE = 'Aggregate';
export const COUNT = 'Count';

export enum EColumnDimensionType {
  CHART_OF_ACCOUNTS = 'opengov/chart-of-accounts',
  DATE = 'opengov/date'
}

// the possible units by which a dataset *might* be organized
export enum CalendarUnit {
  Day = 'day',
  FiscalPeriod = 'fiscalPeriod',
  FiscalQuarter = 'fiscalQuarter',
  FiscalYear = 'fiscalYear',
  Month = 'month',
  Quarter = 'quarter',
  Week = 'week',
  Year = 'year'
}

export enum EReportColumnTypes {
  dimensional = 'dimensional',
  storage = 'storage',
  infix = 'infix', // aka calculated
  conditional = 'conditional',
  percentOfTotal = 'percentOfTotal',
  constant = 'constant',
  aggregate = 'aggregate',
  targetStatus = 'targetStatus'
}

export enum DateColumnName {
  Year = 'calendar_year_actual',
  FiscalYear = 'fiscal_year',
  Quarter = 'calendar_first_day_of_quarter',
  FiscalQuarter = 'fiscal_first_day_of_quarter',
  Month = 'calendar_first_day_of_month',
  Week = 'calendar_first_day_of_week',
  Day = 'calendar_date_actual'
}

// Names used for fiscalyear+period dimension
export enum FiscalYearFiscalPeriodColumnName {
  Year = 'calendar_year',
  FiscalYear = 'fiscal_year',
  Quarter = 'calendar_quarter_name',
  FiscalQuarter = 'fiscal_quarter_name',
  FiscalYearAndPeriod = 'fiscal_year_fiscal_period',
  Month = 'calendar_month_name'
}

export const calendarUnitToDateColumnName = {
  [CalendarUnit.Year]: DateColumnName.Year,
  [CalendarUnit.FiscalYear]: DateColumnName.FiscalYear,
  [CalendarUnit.Quarter]: DateColumnName.Quarter,
  [CalendarUnit.FiscalQuarter]: DateColumnName.FiscalQuarter,
  [CalendarUnit.Month]: DateColumnName.Month,
  [CalendarUnit.Week]: DateColumnName.Week,
  [CalendarUnit.Day]: DateColumnName.Day
};

// When selecting fiscal Period from the dimension we mean fiscal year+ periods
export const calendarUnitToFiscalYearFiscalPeriodColumnName = {
  [CalendarUnit.Year]: FiscalYearFiscalPeriodColumnName.Year,
  [CalendarUnit.FiscalYear]: FiscalYearFiscalPeriodColumnName.FiscalYear,
  [CalendarUnit.Quarter]: FiscalYearFiscalPeriodColumnName.Quarter,
  [CalendarUnit.FiscalQuarter]: FiscalYearFiscalPeriodColumnName.FiscalQuarter,
  [CalendarUnit.FiscalPeriod]: FiscalYearFiscalPeriodColumnName.FiscalYearAndPeriod,
  [CalendarUnit.Month]: FiscalYearFiscalPeriodColumnName.Month
};

export const dateColumnNameToCalendarUnit = {
  [DateColumnName.Year]: CalendarUnit.Year,
  [DateColumnName.FiscalYear]: CalendarUnit.FiscalYear,
  [DateColumnName.Quarter]: CalendarUnit.Quarter,
  [DateColumnName.FiscalQuarter]: CalendarUnit.FiscalQuarter,
  [DateColumnName.Month]: CalendarUnit.Month,
  [DateColumnName.Week]: CalendarUnit.Week,
  [DateColumnName.Day]: CalendarUnit.Day
};

export interface IPercentOfTotalReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.percentOfTotal;
  excludedCategorizedByValues?: List<string>;
  originalColumnType: EReportColumnTypes;
  // if original column type was infix
  operator?: EOgqlInfixColumnOperators;
  leftColumn?: IRecordOf<IReportColumn>;
  rightColumn?: IRecordOf<IReportColumn>;
  // if original column type was conditional
  sourceColumn?: IReportColumn;
  conditions?: List<IConditionalExpressionCondition<any>>;
  else?: IReportColumn;
}

export enum EOgqlInfixColumnOperators {
  add = '+',
  divide = '/',
  multiply = '*',
  subtract = '-'
}

export enum EOgqlAggregateColumnOperators {
  count = 'count',
  sum = 'sum'
}

export enum EConditionalComparisonOperators {
  greaterThan = 'greaterThan',
  greaterThanOrEqualTo = 'greaterThanOrEqualTo',
  lessThan = 'lessThan',
  lessThanOrEqualTo = 'lessThanOrEqualTo',
  equalTo = 'equalTo',
  notEqualTo = 'notEqualTo',
  between = 'between' // inclusive between e.g. `10 <= x <= 20`
}

export enum EReportConstant {
  Day = 'day',
  Month = 'month',
  Week = 'week',
  Quarter = 'quarter',
  Year = 'year',
  Number = 'number',
  Percentage = 'percentage',
  Date = 'date',
  Text = 'text'
}

export interface IConstantReportColumn<ValueType extends string | number | Date> extends IBaseReportColumn {
  columnType: EReportColumnTypes.constant;
  value: ValueType;
  unit: EReportConstant;
}

export interface IStorageReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.storage;
}

export interface IDimensionalReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.dimensional;
  dimensionType: EColumnDimensionType;
  dimensionId: string;
  joinColumnName?: string;
  accountCodeColumnRef?: string; // for the query and summary table
  descriptionColumnRef: string; // for the query and summary table
}

export interface IDimensionalDateReportColumn extends IDimensionalReportColumn {
  dateColumnName: DateColumnName | string;
}

export interface IInfixReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.infix;
  operator: EOgqlInfixColumnOperators;
  leftColumn: IReportColumn; // better names
  rightColumn: IReportColumn;
}

export interface IConditionalReportColumn<OutputColumnType extends string | number | Date> extends IBaseReportColumn {
  columnType: EReportColumnTypes.conditional;
  sourceColumn: IReportColumn;
  conditions: List<IConditionalExpressionCondition<OutputColumnType>>;
  else: OutputColumnType;
}

export interface ITargetStatusReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.targetStatus;
}

// IConditionalExpressionComparisonValue is the value(s) we are comparing against
export type IConditionalExpressionComparisonValue<OutputColumnType extends string | number | Date> = OutputColumnType;

export type IConditionalExpressionCondition<OutputColumnType extends string | number | Date> = {
  operator: EConditionalComparisonOperators;
  lowerBound?: IConditionalExpressionComparisonValue<any>;
  upperBound?: IConditionalExpressionComparisonValue<any>;
  matchValue?: IConditionalExpressionComparisonValue<any>;
  returnedValue: OutputColumnType;
};

export const newCondition = RecordOf<IConditionalExpressionCondition<any>>({
  operator: null,
  matchValue: null,
  returnedValue: null
});

export const NewCondition = (rs?: Partial<IConditionalExpressionCondition<any>>) =>
  !rs
    ? newCondition()
    : newCondition(rs).set('matchValue', mapColumnToRecord(rs.matchValue as IRecordOf<IReportColumn>));

export const newConditionBetween = RecordOf<IConditionalExpressionCondition<any>>({
  operator: null,
  lowerBound: null,
  upperBound: null,
  returnedValue: null
});

export const NewConditionBetween = (rs?: Partial<IConditionalExpressionCondition<any>>) =>
  !rs
    ? newConditionBetween()
    : newConditionBetween(rs)
      .set('lowerBound', mapColumnToRecord(rs.lowerBound as IRecordOf<IReportColumn>))
      .set('upperBound', mapColumnToRecord(rs.upperBound as IRecordOf<IReportColumn>));

export interface IAggregateReportColumn extends IBaseReportColumn {
  columnType: EReportColumnTypes.aggregate;
  name: EOgqlAggregateColumnOperators;
  aggregateType: EOgqlAggregateColumnOperators;
}

export type IReportColumnMetaDataTags = {
  hidden?: boolean;
  units?: string;
};

export type IReportColumnMetaDataFacts = {
  distinctCount?: string; // OGQL returns strings even for numeric values
  minimum?: string;
  maximum?: string;
};

export const ReportColumnMetaDataTagsRecord = RecordOf<IReportColumnMetaDataTags>({
  hidden: null,
  units: null
});

export const ReportColumnMetaDataFactsRecord = RecordOf<IReportColumnMetaDataFacts>({
  distinctCount: null,
  minimum: null,
  maximum: null
});

export interface IReportColumnMetadata {
  tags: IRecordOf<IReportColumnMetaDataTags>;
  facts: IRecordOf<IReportColumnMetaDataFacts>;
  units?: string;
}

export const reportColumnMetaDataRecord = RecordOf<IReportColumnMetadata>({
  tags: ReportColumnMetaDataTagsRecord(),
  facts: ReportColumnMetaDataFactsRecord(),
  units: null
});

export const ogqlSemanticTypeRecord = RecordOf<IOgqlSemanticType>({
  type: null,
  name: null
});

const ogqlSemanticTypeRecordOrString = (rs: IOgqlSemanticType | string) => {
  if ((rs as IOgqlSemanticType).type) {
    return ogqlSemanticTypeRecord(rs as IOgqlSemanticType);
  }

  return rs;
};

export const ReportColumnMetaDataRecord = (rs?: Partial<IReportColumnMetadata>): IRecordOf<IReportColumnMetadata> =>
  !rs
    ? reportColumnMetaDataRecord()
    : reportColumnMetaDataRecord(rs)
      .set('tags', rs.tags ? ReportColumnMetaDataTagsRecord(rs.tags) : ReportColumnMetaDataTagsRecord())
      .set('facts', rs.facts ? ReportColumnMetaDataFactsRecord(rs.facts) : ReportColumnMetaDataFactsRecord());

export interface IBaseReportColumn {
  name: string;
  displayName: string;
  originalDisplayName?: string;
  type?: BasicColumnTypes;
  id?: string;
  active?: boolean;
  hidden?: boolean;
  // Delphius produces IOgqlSemanticType where lenses produce string
  semanticTypes?: List<IOgqlSemanticType | string>;
  metadata?: IRecordOf<IReportColumnMetadata>;
}

export type IReportColumn =
  | IDimensionalReportColumn
  | IStorageReportColumn
  | IConditionalReportColumn<any>
  | IInfixReportColumn
  | IPercentOfTotalReportColumn
  | IConstantReportColumn<any>
  | IAggregateReportColumn
  | ITargetStatusReportColumn;

export const storageReportColumnRecord = RecordOf<IStorageReportColumn>({
  columnType: EReportColumnTypes.storage,
  displayName: '',
  hidden: false,
  metadata: ReportColumnMetaDataRecord(),
  name: '',
  semanticTypes: List<IOgqlSemanticType | string>(),
  type: BasicColumnTypes.Text
});

export const dimensionalReportColumnRecord = RecordOf<IDimensionalReportColumn>({
  accountCodeColumnRef: null,
  descriptionColumnRef: null,
  columnType: EReportColumnTypes.dimensional,
  dimensionId: null,
  dimensionType: null,
  displayName: '',
  hidden: false,
  metadata: ReportColumnMetaDataRecord(),
  name: '',
  semanticTypes: List<string>(),
  type: BasicColumnTypes.Text,
  joinColumnName: null
});

const dimensionalDateReportColumnRecord = RecordOf<IDimensionalDateReportColumn>({
  accountCodeColumnRef: null,
  descriptionColumnRef: null,
  columnType: EReportColumnTypes.dimensional,
  dateColumnName: '',
  dimensionId: null,
  dimensionType: EColumnDimensionType.DATE,
  displayName: '',
  hidden: false,
  metadata: ReportColumnMetaDataRecord(),
  name: '',
  semanticTypes: List<string>(),
  type: BasicColumnTypes.Text
});

export type IInfixReportColumnRecord = IRecordOf<IInfixReportColumn>;

export type IConditionalReportColumnRecord = IRecordOf<IConditionalReportColumn<any>>;

export type IStorageReportColumnRecord = IRecordOf<IStorageReportColumn>;

export type IPercentOfTotalReportColumnRecord = IRecordOf<IPercentOfTotalReportColumn>;

export type IAggregateReportColumnRecord = IRecordOf<IAggregateReportColumn>;

export type ITargetStatusReportColumnRecord = IRecordOf<ITargetStatusReportColumn>;

export const DimensionalReportColumnRecord = (
  rs?: Partial<IDimensionalReportColumn>
): IRecordOf<IDimensionalReportColumn> =>
  !rs
    ? dimensionalReportColumnRecord()
    : dimensionalReportColumnRecord(rs)
      .set('semanticTypes', rs.semanticTypes ? List(rs.semanticTypes.map(ogqlSemanticTypeRecordOrString)) : List())
      .set('metadata', rs.metadata ? ReportColumnMetaDataRecord(rs.metadata) : ReportColumnMetaDataRecord());

export const DimensionalDateReportColumnRecord = (
  rs?: Partial<IDimensionalDateReportColumn>
): IRecordOf<IDimensionalDateReportColumn> =>
  !rs
    ? dimensionalDateReportColumnRecord()
    : dimensionalDateReportColumnRecord(rs)
      .set('semanticTypes', rs.semanticTypes ? List(rs.semanticTypes.map(ogqlSemanticTypeRecordOrString)) : List())
      .set('metadata', rs.metadata ? ReportColumnMetaDataRecord(rs.metadata) : ReportColumnMetaDataRecord());

export const StorageReportColumnRecord = (rs?: Partial<IStorageReportColumn>): IStorageReportColumnRecord =>
  !rs
    ? storageReportColumnRecord()
    : storageReportColumnRecord(rs)
      .set('semanticTypes', rs.semanticTypes ? List(rs.semanticTypes.map(ogqlSemanticTypeRecordOrString)) : List())
      .set('metadata', rs.metadata ? ReportColumnMetaDataRecord(rs.metadata) : ReportColumnMetaDataRecord());

export const InfixReportColumnRecord = RecordOf<IInfixReportColumn>({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.infix,
  hidden: false,
  leftColumn: StorageReportColumnRecord(),
  rightColumn: StorageReportColumnRecord(),
  operator: null
});

export const PercentOfTotalReportColumnRecord = (
  rs?: Partial<IPercentOfTotalReportColumn>
): IPercentOfTotalReportColumnRecord =>
  !rs
    ? percentOfTotalReportColumnRecord()
    : percentOfTotalReportColumnRecord(rs)
      .set('semanticTypes', rs.semanticTypes ? List(rs.semanticTypes.map(ogqlSemanticTypeRecordOrString)) : List())
      .set('metadata', rs.metadata ? ReportColumnMetaDataRecord(rs.metadata) : ReportColumnMetaDataRecord())
      .set(
        'excludedCategorizedByValues',
        rs.excludedCategorizedByValues ? List(rs.excludedCategorizedByValues) : List()
      );

export const percentOfTotalReportColumnRecord = RecordOf<IPercentOfTotalReportColumn>({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.percentOfTotal,
  hidden: false,
  excludedCategorizedByValues: List(),
  originalColumnType: EReportColumnTypes.storage,
  sourceColumn: null,
  conditions: null,
  else: null,
  leftColumn: null,
  rightColumn: null,
  operator: null
});

export const ConstantReportColumnRecord = RecordOf<IConstantReportColumn<any>>({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.constant,
  hidden: false,
  value: null,
  unit: EReportConstant.Number
});

export const ConditionalReportColumnRecord = (
  rs?: Partial<IConditionalReportColumn<any>>
): IConditionalReportColumnRecord =>
  !rs
    ? conditionalReportColumnRecord()
    : conditionalReportColumnRecord(rs)
      .set('semanticTypes', rs.semanticTypes ? List(rs.semanticTypes.map(ogqlSemanticTypeRecordOrString)) : List())
      .set('metadata', rs.metadata ? ReportColumnMetaDataRecord(rs.metadata) : ReportColumnMetaDataRecord())
      .set('sourceColumn', rs.sourceColumn ? mapColumnToRecord(rs.sourceColumn) : StorageReportColumnRecord())
      .set('conditions', rs.conditions ? List(getListOfConditions(rs.conditions)) : List());

export const getListOfConditions = (conditions) => {
  const conditionList = [];
  conditions.forEach((condition) => {
    condition.operator === EConditionalComparisonOperators.between
      ? conditionList.push(NewConditionBetween(condition))
      : conditionList.push(NewCondition(condition));
  });
  return conditionList;
};

export const conditionalReportColumnRecord = RecordOf<IConditionalReportColumn<any>>({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.conditional,
  hidden: false,
  sourceColumn: StorageReportColumnRecord(),
  conditions: List(),
  else: '',
  metadata: ReportColumnMetaDataRecord()
});

export const AggregateReportColumnRecord = RecordOf<IAggregateReportColumn>({
  name: EOgqlAggregateColumnOperators.count,
  aggregateType: EOgqlAggregateColumnOperators.count,
  displayName: COUNT,
  type: BasicColumnTypes.Numeric,
  columnType: EReportColumnTypes.aggregate,
  hidden: false
});

export const TargetStatusReportColumnRecord = RecordOf<ITargetStatusReportColumn>({
  name: EReportColumnTypes.targetStatus,
  displayName: 'Target Status',
  type: BasicColumnTypes.Numeric,
  columnType: EReportColumnTypes.targetStatus,
  hidden: false
});

export const categorizedByNoneColumnObject = {
  name: EOgqlAggregateColumnOperators.sum,
  aggregateType: EOgqlAggregateColumnOperators.sum,
  displayName: AGGREGATE,
  type: BasicColumnTypes.Numeric,
  columnType: EReportColumnTypes.aggregate,
  hidden: false
};

export const mapColumnToRecord = (column: IReportColumn) => {
  if (!column) return;
  switch (column.columnType) {
    case EReportColumnTypes.dimensional:
      if ('dateColumnName' in column) {
        const dateColumn = column as IDimensionalDateReportColumn;
        return DimensionalDateReportColumnRecord(dateColumn);
      } else {
        return DimensionalReportColumnRecord(column);
      }
    case EReportColumnTypes.infix:
      return InfixReportColumnRecord(column)
        .set('leftColumn', mapColumnToRecord(column.leftColumn))
        .set('rightColumn', mapColumnToRecord(column.rightColumn));
    case EReportColumnTypes.conditional:
      return ConditionalReportColumnRecord(column);
    case EReportColumnTypes.constant:
      return ConstantReportColumnRecord(column);
    case EReportColumnTypes.percentOfTotal:
      return PercentOfTotalReportColumnRecord(column);
    case EReportColumnTypes.aggregate:
      return AggregateReportColumnRecord(column);
    case EReportColumnTypes.targetStatus:
      return TargetStatusReportColumnRecord(column);
    case EReportColumnTypes.storage:
    default:
      if (column.type && column.type.includes('logical/bool')) {
        return StorageReportColumnRecord({
          ...column,
          type: BasicColumnTypes.Boolean
        });
      }
      return StorageReportColumnRecord(column);
  }
};

export const getDisplayName = (
  column: IRecordOf<IReportColumn> | IRecordOf<IDimensionalReportColumn>,
  columnNames: List<IRecordOf<IColumnAlias>>,
  dataMgrColumn?: IOgqlColumn | IDatasetLensColumn
): string => {
  // apparently sometimes columnNames doesn't have a find method, even if it's an Immutable List
  const customSettingsForColumn = columnNames.find && columnNames.find((r) => r.originalName === column.name);
  if (customSettingsForColumn) {
    return customSettingsForColumn.displayName;
  }
  if (dataMgrColumn) {
    if ('label' in dataMgrColumn) {
      return dataMgrColumn.label;
    } else {
      return dataMgrColumn.displayName;
    }
  }
  return column.displayName;
};

export const getColumnWithSimpleName = (column: IRecordOf<IReportColumn>): IRecordOf<IReportColumn> => {
  if (!column) {
    return;
  }
  if (column.columnType === EReportColumnTypes.infix) {
    return column
      .setIn(['name'], getColumnName(column.name))
      .setIn(['leftColumn'], getColumnWithSimpleName(mapColumnToRecord(column.leftColumn)))
      .setIn(['rightColumn'], getColumnWithSimpleName(mapColumnToRecord(column.rightColumn)));
  }
  return column.setIn(['name'], getColumnName(column.name));
};
